import { h as __h } from "preact";
import { signal } from "@preact/signals";
import { Component, h } from "preact";
import { checkoutSessionResponse, enterpriseAPIKeyResponse, paymentMethodsResponse, ssoConfigLinkResponse, ssoProviderResponse } from "~/backend/validate.js";
/** @import { domainRefResponse, paymentMethodResponse, pendingVerification } from "~/backend/validate.js"; */
import { ApiError, authedApiRequest, query, response } from "~/lib/api.js";
import { identity } from "~/lib/identity.js";
import { alert } from "~/util/alert.js";
import html from "~/util/html.js";
import { PromiseSignal } from "~/util/promise.js";
import Query from "~/util/query.js";
import { navigate, pathname, search } from "~/util/router.js";
import { array } from "~/util/validate.js";
import Banner from "~/component/Banner.js";
import Button from "~/component/Button.js";
import { confirmModal } from "~/component/ConfirmModal.js";
import Data from "~/component/Data.js";
import Dropdown from "~/component/Dropdown.js";
/** @import { DropdownItem } from "~/component/Dropdown.js"; */
import Icon from "~/component/Icon.js";
import Section from "~/component/Section.js";
import Time, { relative } from "~/component/Time.js";
import { Crumb } from "~/admin/component/Breadcrumbs.js";
import DisabledTooltip from "~/admin/component/DisabledTooltip.js";
import FreshKeyCallout from "~/admin/component/FreshKeyCallout.js";
/** @import { FreshKey } from "~/admin/component/FreshKeyCallout.js"; */

import adminStyle from "~/admin/lib/style.js";
import { addApiKey } from "~/admin/res/apikey/actions/addApiKey.js";
import { deleteApiKey } from "~/admin/res/apikey/actions/deleteApiKey.js";
import { editApiKey } from "~/admin/res/apikey/actions/editApiKey.js";
import { expireApiKey } from "~/admin/res/apikey/actions/expireApiKey.js";
import { addSsoProvider } from "~/admin/res/ssoprovider/actions/addSsoProvider.js";
import { renameSsoProvider } from "~/admin/res/ssoprovider/actions/renameSsoProvider.js";
import button from "~/style/button.js";
import link from "~/style/link.js";
import util from "~/style/util.js";
/** @import { sessionEnterpriseResponse } from "~/backend/validate.js"; */
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./Settings.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./Settings.module.css", import.meta.url);
const css = adopt(sheet);

/**
 * @extends {Component<SettingsProps>}
 *
 * @typedef {object} SettingsProps
 * @property {sessionEnterpriseResponse} enterprise
 */
export default class Settings extends Component {
  render() {
    const enterprise = this.props.enterprise;
    return [__h(Crumb, {}, "Settings"), __h("div", {
      "class": adminStyle.main
    }, __h("div", {
      "class": css.body
    }, identity.permission("enterprise", "settings") && [__h(Section, {
      title: "Security",
      icon: "password"
    }, __h("div", {
      "class": css.settings
    }, __h(Tfa, {
      enterprise: enterprise
    }), enterprise.is_workos_configured && __h(GroupSync, {
      enterprise: enterprise
    }))), __h(SsoProviders, {
      enterprise: enterprise
    })], identity.permission("enterprise", "api_keys") && __h(ApiKeys, {
      enterpriseId: enterprise.id
    }), identity.permission("enterprise", "billing") && __h(PaymentMethods, {
      enterpriseId: enterprise.id
    })))];
  }
}

/**
 * @extends {Component<TfaProps>}
 *
 * @typedef {object} TfaProps
 * @property {sessionEnterpriseResponse} enterprise
 */
class Tfa extends Component {
  #toggle = new PromiseSignal(async () => {
    const isTfaRequired = this.props.enterprise.is_tfa_required;
    try {
      await authedApiRequest("PUT", `/v2/enterprises/${this.props.enterprise.id}/tfa`, {
        json: {
          is_tfa_required: !isTfaRequired
        }
      });
      identity.fetch();
    } catch (err) {
      alert(ApiError.from(err).message, {
        type: "error"
      });
    }
  });
  render() {
    const isTfaRequired = this.props.enterprise.is_tfa_required;
    return __h("div", {
      "class": css.settingRow
    }, __h("div", {
      "class": css.settingInfo
    }, __h("span", {
      "class": css.settingLabel
    }, "Two-factor authentication"), __h("span", {
      "class": css.settingDescription
    }, "Require all members to set up two-factor authentication")), __h("div", {
      "class": css.settingActions
    }, __h(Button, {
      kind: isTfaRequired ? "negative" : "primary",
      level: isTfaRequired ? "secondary" : "primary",
      loading: this.#toggle.loading,
      onClick: this.#toggle.invoke
    }, isTfaRequired ? "Disable 2FA requirement" : "Require 2FA")));
  }
}

/**
 * @extends {Component<GroupSyncProps>}
 *
 * @typedef {object} GroupSyncProps
 * @property {sessionEnterpriseResponse} enterprise
 */
class GroupSync extends Component {
  #toggle = new PromiseSignal(async () => {
    try {
      await authedApiRequest("PUT", `/v2/enterprises/${this.props.enterprise.id}/group-sync`, {
        json: {
          is_group_sync_enabled: !this.props.enterprise.is_group_sync_enabled
        }
      });
    } catch (err) {
      alert(ApiError.from(err).message, {
        type: "error"
      });
    }
    // Refetch even on failure: a 409 means the session's SCIM state is stale
    Query.invalidate();
    await identity.fetch();
  });
  #onClick = async () => {
    if (!this.props.enterprise.is_group_sync_enabled) {
      const confirm = await confirmModal({
        title: "Enable directory mappings?",
        action: "Enable",
        kind: "primary",
        body: __h("p", {}, "Phaze will apply your directory mappings to every directory user now, removing memberships no\n\t\t\t\t\t\tmapping grants. Memberships can't be edited by hand while mappings are on.")
      });
      if (confirm !== true) return;
    }
    await this.#toggle.invoke();
  };
  render() {
    const enabled = this.props.enterprise.is_group_sync_enabled;
    const canSync = this.props.enterprise.is_scim_enabled;
    return __h("div", {
      "class": css.settingRow
    }, __h("div", {
      "class": css.settingInfo
    }, __h("span", {
      "class": css.settingLabel
    }, "Directory mappings"), __h("span", {
      "class": css.settingDescription
    }, "Grant organizations, groups and admin roles from directory groups.", " ", __h("a", {
      "class": link.link,
      href: "/mappings"
    }, "Manage mappings"))), __h("div", {
      "class": css.settingActions
    }, __h(DisabledTooltip, {
      tooltip: !enabled && !canSync && "Enable directory sync to use mappings"
    }, __h(Button, {
      kind: enabled ? "negative" : "primary",
      level: enabled ? "secondary" : "primary",
      disabled: !enabled && !canSync,
      loading: this.#toggle.loading,
      onClick: this.#onClick
    }, enabled ? "Turn off mappings" : "Turn on mappings"))));
  }
}

// a function, since several providers can render it at once
const noDomains = () => __h("p", {
  "class": css.empty
}, "No domains yet. Add a domain to verify ownership and enable SSO.");

/**
 * Redirects to the WorkOS admin portal for one of a provider's settings.
 *
 * @param {string} enterpriseId
 * @param {string} providerId
 * @param {"sso" | "scim" | "domain_verification"} intent
 */
async function openConfigLink(enterpriseId, providerId, intent) {
  try {
    const {
      data
    } = await authedApiRequest("GET", `/v2/enterprises/${enterpriseId}/sso-providers/${providerId}/config-link`, {
      query: {
        intent
      },
      schema: response(ssoConfigLinkResponse)
    });
    window.location.assign(data.link);
  } catch (err) {
    alert(ApiError.from(err).message, {
      type: "error"
    });
  }
}

/**
 * @extends {Component<SsoProvidersProps>}
 *
 * @typedef {object} SsoProvidersProps
 * @property {sessionEnterpriseResponse} enterprise
 */
class SsoProviders extends Component {
  providers = query(() => ({
    key: [`/v2/enterprises/${this.props.enterprise.id}/sso-providers`]
  }), {
    schema: response(array(ssoProviderResponse))
  });

  // the first domain creates the enterprise's first provider, named after the enterprise
  #addFirstDomain = new PromiseSignal(async () => {
    const enterprise = this.props.enterprise;
    try {
      const {
        data: provider
      } = await authedApiRequest("POST", `/v2/enterprises/${enterprise.id}/sso-providers`, {
        json: {
          name: enterprise.name
        },
        schema: response(ssoProviderResponse)
      });
      this.providers.fetch();
      await openConfigLink(enterprise.id, provider.id, "domain_verification");
    } catch (err) {
      alert(ApiError.from(err).message, {
        type: "error"
      });
    }
  });

  // like the first domain, a new provider goes straight to adding its domain
  #addProvider = async () => {
    const enterpriseId = this.props.enterprise.id;
    const provider = await addSsoProvider({
      enterpriseId
    });
    if (provider) await openConfigLink(enterpriseId, provider.id, "domain_verification");
  };

  /** @param {ssoProviderResponse} provider */
  #removeProvider = async provider => {
    if (!(await confirmModal({
      title: `Remove ${provider.name}?`,
      action: "Remove",
      body: __h("p", {}, "This also removes its domains and its SSO and directory sync configuration.")
    }))) return;
    try {
      await authedApiRequest("DELETE", `/v2/enterprises/${this.props.enterprise.id}/sso-providers/${provider.id}`);
      this.providers.fetch();
      identity.fetch();
    } catch (err) {
      alert(ApiError.from(err).message, {
        type: "error"
      });
    }
  };

  /** @param {domainRefResponse} domain */
  #removeDomain = async domain => {
    if (!(await confirmModal({
      title: `Remove ${domain.domain}?`,
      action: "Remove",
      body: __h("p", {}, "Members will no longer be able to sign in through SSO with this email domain.")
    }))) return;
    try {
      await authedApiRequest("DELETE", `/v2/enterprises/${this.props.enterprise.id}/domains/${domain.id}`);
      this.providers.fetch();
      identity.fetch();
    } catch (err) {
      alert(ApiError.from(err).message, {
        type: "error"
      });
    }
  };
  render() {
    const providers = this.providers.data?.data;
    return __h(Section, {
      title: "Single sign-on",
      icon: "office",
      bare: !!(providers?.length && !this.providers.error),
      actions: providers && (providers.length ? __h(Button, {
        size: "small",
        kind: "primary",
        level: "tertiary",
        onClick: this.#addProvider
      }, "Add provider") : __h(Button, {
        size: "small",
        kind: "primary",
        level: "tertiary",
        loading: this.#addFirstDomain.loading,
        onClick: this.#addFirstDomain.invoke
      }, "Add domain"))
    }, h(Data, {
      loading: this.providers.loading && !providers,
      error: this.providers.error,
      empty: providers && !providers.length && noDomains(),
      children: providers && __h("div", {
        "class": css.settings
      }, providers.map(provider => __h(SsoProvider, {
        key: provider.id,
        enterpriseId: this.props.enterprise.id,
        provider: provider,
        onRemoveDomain: this.#removeDomain,
        onRename: () => renameSsoProvider({
          enterpriseId: this.props.enterprise.id
        }, provider),
        onRemove: () => this.#removeProvider(provider)
      })))
    }));
  }
}

/**
 * @extends {Component<SsoProviderProps>}
 *
 * @typedef {object} SsoProviderProps
 * @property {string} enterpriseId
 * @property {ssoProviderResponse} provider
 * @property {(domain: domainRefResponse) => void} onRemoveDomain
 * @property {() => void} onRename
 * @property {() => void} onRemove
 */
class SsoProvider extends Component {
  #configureSso = new PromiseSignal(() => openConfigLink(this.props.enterpriseId, this.props.provider.id, "sso"));
  #configureScim = new PromiseSignal(() => openConfigLink(this.props.enterpriseId, this.props.provider.id, "scim"));
  #addDomain = new PromiseSignal(() => openConfigLink(this.props.enterpriseId, this.props.provider.id, "domain_verification"));
  render() {
    const {
      provider
    } = this.props;
    const {
      domains
    } = provider;
    const canSSO = domains.some(d => d.state === "verified");
    const canSCIM = provider.is_sso_enabled;
    const isActive = provider.is_sso_enabled || provider.is_scim_enabled;
    return __h("div", {
      "class": util.card + " " + css.card
    }, __h("div", {
      "class": css.providerHeader
    }, __h("span", {
      "class": css.providerName
    }, provider.name), __h(Dropdown, {
      id: "provider-menu-" + provider.id,
      placement: "bottom-end",
      trigger: __h("button", {
        type: "button",
        "class": button.button,
        "data-level": "tertiary",
        "data-size": "small",
        popovertarget: "provider-menu-" + provider.id,
        "aria-label": "Options for " + provider.name
      }, __h(Icon, {
        name: "menu",
        size: 14
      })),
      items: [{
        icon: "pencil",
        text: "Rename provider",
        onSelect: this.props.onRename
      }, {
        icon: "trash",
        text: "Remove provider",
        kind: "negative",
        disabled: isActive,
        tooltip: isActive ? "Turn off SSO and directory sync to remove this provider" : undefined,
        onSelect: this.props.onRemove
      }]
    })), __h("div", {
      "class": css.settingRow
    }, __h("div", {
      "class": css.settingInfo
    }, __h("span", {
      "class": css.settingLabel
    }, "Domains")), __h(Button, {
      size: "small",
      kind: "primary",
      level: "tertiary",
      loading: this.#addDomain.loading,
      onClick: this.#addDomain.invoke
    }, "Add domain")), domains.length ? __h("ul", {
      "class": css.list
    }, domains.map(domain => this.#domainRow(domain))) : noDomains(), __h("div", {
      "class": css.settingRow
    }, __h("div", {
      "class": css.settingInfo
    }, __h("span", {
      "class": css.settingLabel
    }, "SSO"), __h("span", {
      "class": css.settingDescription
    }, "Manage your enterprise's SSO settings")), __h("div", {
      "class": css.settingActions
    }, this.#chip(provider.is_sso_enabled), __h(DisabledTooltip, {
      tooltip: !canSSO && "Verify a domain to enable SSO"
    }, __h(Button, {
      level: "secondary",
      disabled: !canSSO,
      loading: this.#configureSso.loading,
      onClick: this.#configureSso.invoke
    }, "Configure SSO")))), __h("div", {
      "class": css.settingRow
    }, __h("div", {
      "class": css.settingInfo
    }, __h("span", {
      "class": css.settingLabel
    }, "Directory sync"), __h("span", {
      "class": css.settingDescription
    }, "Manage directory sync and user provisioning")), __h("div", {
      "class": css.settingActions
    }, this.#chip(provider.is_scim_enabled), __h(DisabledTooltip, {
      tooltip: !canSCIM && "Enable SSO to configure directory sync"
    }, __h(Button, {
      level: "secondary",
      disabled: !canSCIM,
      loading: this.#configureScim.loading,
      onClick: this.#configureScim.invoke
    }, "Configure directory sync")))));
  }

  /** @param {boolean} on */
  #chip(on) {
    return __h("span", {
      "class": css.statusChip,
      "data-state": on ? "on" : "off"
    }, on ? "On" : "Off");
  }

  /** @param {domainRefResponse} domain */
  #domainRow(domain) {
    return __h("li", {
      key: domain.id,
      "class": css.listItem
    }, __h("span", {
      "class": css.listItemName
    }, domain.domain), __h("div", {
      "class": css.listItemActions
    }, __h("span", {
      "class": css.statusChip,
      "data-state": domain.state
    }, domain.state), __h(Dropdown, {
      id: "domain-menu-" + domain.id,
      placement: "bottom-end",
      trigger: __h("button", {
        type: "button",
        "class": button.button,
        "data-level": "tertiary",
        "data-size": "small",
        popovertarget: "domain-menu-" + domain.id,
        "aria-label": "Options for " + domain.domain
      }, __h(Icon, {
        name: "menu",
        size: 14
      })),
      items: [{
        icon: "trash",
        text: "Remove",
        kind: "negative",
        onSelect: () => this.props.onRemoveDomain(domain)
      }]
    })));
  }
}

/**
 * @extends {Component<ApiKeysProps>}
 *
 * @typedef {object} ApiKeysProps
 * @property {string} enterpriseId
 */
class ApiKeys extends Component {
  apiKeys = query(() => ({
    key: [`/v2/enterprises/${this.props.enterpriseId}/api-keys`]
  }), {
    schema: response(array(enterpriseAPIKeyResponse))
  });
  freshKey = signal(/** @type {FreshKey | null} */null);
  addApiKey = async () => {
    const res = await addApiKey({
      enterpriseId: this.props.enterpriseId
    });
    if (res) this.freshKey.value = res;
  };
  render() {
    const apiKeys = this.apiKeys;
    const rows = apiKeys.data?.data;
    return __h(Section, {
      title: "API keys",
      icon: "key",
      actions: __h(Button, {
        size: "small",
        kind: "primary",
        level: "tertiary",
        onClick: this.addApiKey
      }, "Create key")
    }, __h("div", {
      "class": css.list
    }, __h(FreshKeyCallout, {
      freshKey: this.freshKey.value
    }), h(Data, {
      loading: apiKeys.loading && !apiKeys.data,
      error: apiKeys.error,
      empty: rows && !rows.length && __h("p", {
        "class": css.empty
      }, "No API keys yet. Create a key to access the developer API on behalf of your\n\t\t\t\t\t\t\t\t\tenterprise."),
      children: rows && __h("ul", {
        "class": css.list
      }, rows.map(apiKey => this.#row(apiKey)))
    })));
  }

  /** @param {enterpriseAPIKeyResponse} apiKey */
  #status(apiKey) {
    if (apiKey.is_expired) return {
      state: "expired",
      text: "Expired"
    };
    if (apiKey.expires_at) {
      return {
        state: "expiring",
        text: __h("span", {}, "Expires ", __h(Time, {
          fmt: relative,
          datetime: new Date(apiKey.expires_at),
          tick: true
        }))
      };
    }
    return {
      state: "active",
      text: "Active"
    };
  }

  /** @param {enterpriseAPIKeyResponse} apiKey */
  #row(apiKey) {
    const props = {
      enterpriseId: this.props.enterpriseId
    };
    const status = this.#status(apiKey);
    return __h("li", {
      key: apiKey.id,
      "class": css.listItem
    }, __h("div", {
      "class": css.listItemInfo
    }, __h("span", {
      "class": css.listItemName
    }, apiKey.name), __h("span", {
      "class": css.listItemMeta
    }, __h("code", {
      "class": css.keyHint
    }, apiKey.key_hint), apiKey.description && __h("span", {}, apiKey.description), __h("span", {}, apiKey.last_used_at ? ["Last used", " ", __h(Time, {
      fmt: relative,
      datetime: new Date(apiKey.last_used_at)
    })] : "Never used"))), __h("div", {
      "class": css.listItemActions
    }, __h("span", {
      "class": css.statusChip,
      "data-state": status.state
    }, status.text), __h(Dropdown, {
      id: "api-key-menu-" + apiKey.id,
      placement: "bottom-end",
      trigger: __h("button", {
        type: "button",
        "class": button.button,
        "data-level": "tertiary",
        "data-size": "small",
        popovertarget: "api-key-menu-" + apiKey.id,
        "aria-label": "Options for " + apiKey.name
      }, __h(Icon, {
        name: "menu",
        size: 14
      })),
      items: [{
        icon: "pencil",
        text: "Edit",
        onSelect: () => editApiKey(props, apiKey)
      }, {
        icon: "warning",
        text: "Expire",
        disabled: apiKey.is_expired,
        onSelect: () => expireApiKey(props, apiKey)
      }, {
        icon: "trash",
        text: "Delete",
        kind: "negative",
        onSelect: () => deleteApiKey(props, apiKey)
      }]
    })));
  }
}

/**
 * @extends {Component<PaymentMethodsProps>}
 *
 * @typedef {object} PaymentMethodsProps
 * @property {string} enterpriseId
 */
class PaymentMethods extends Component {
  /** @type {Record<string, string>} */
  static CARD_BRANDS = {
    amex: "American Express",
    diners: "Diners Club",
    discover: "Discover",
    eftpos_au: "eftpos",
    jcb: "JCB",
    mastercard: "Mastercard",
    unionpay: "UnionPay",
    visa: "Visa"
  };

  /** @param {paymentMethodResponse | pendingVerification} method */
  #paymentMethodName(method) {
    if ("card" in method && method.card) {
      const brand = PaymentMethods.CARD_BRANDS[method.card.brand] ?? method.card.brand;
      return `${brand} •••• ${method.card.last4}`;
    }
    if (method.us_bank_account) {
      return `${method.us_bank_account.bank_name} •••• ${method.us_bank_account.last4}`;
    }
    return method.type;
  }

  /** @param {paymentMethodResponse | pendingVerification} method */
  #paymentMethodDetail(method) {
    if ("card" in method && method.card) {
      const month = `${method.card.exp_month}`.padStart(2, "0");
      const year = `${method.card.exp_year}`.slice(-2);
      return `Card • Expiring ${month}/${year}`;
    }
    if ("verification_url" in method) return "Bank account • Unverified";
    if (method.us_bank_account) return "Bank account";
    return undefined;
  }
  #paymentMethods = query(() => ({
    key: [`/v2/enterprises/${this.props.enterpriseId}/billing/payment-methods`]
  }), {
    schema: response(paymentMethodsResponse)
  });

  /** @override */
  componentDidMount() {
    // stripe sends the user back here with the outcome in the query string
    const outcome = search.value.checkout;
    if (!outcome) return;
    if (outcome === "success") alert("Payment method added.", {
      type: "success"
    });
    navigate(pathname.value, {
      replace: true
    });
  }
  #addPaymentMethod = new PromiseSignal(async () => {
    const returnUrl = new URL(location.href);
    returnUrl.searchParams.set("checkout", "success");
    const successUrl = returnUrl.href;
    returnUrl.searchParams.set("checkout", "cancel");
    const cancelUrl = returnUrl.href;
    try {
      const {
        data
      } = await authedApiRequest("POST", `/v2/enterprises/${this.props.enterpriseId}/billing/checkout-session`, {
        json: {
          success_url: successUrl,
          cancel_url: cancelUrl
        },
        schema: response(checkoutSessionResponse)
      });
      window.location.assign(data.url);
    } catch (err) {
      alert(ApiError.from(err).message, {
        type: "error"
      });
    }
  });

  /** @param {paymentMethodResponse} method */
  #setDefault = async method => {
    try {
      await authedApiRequest("PUT", `/v2/enterprises/${this.props.enterpriseId}/billing/payment-methods/${method.id}/default`);
      this.#paymentMethods.fetch();
    } catch (err) {
      alert(ApiError.from(err).message, {
        type: "error"
      });
    }
  };

  /** @param {paymentMethodResponse} method */
  #removePaymentMethod = async method => {
    const removed = await confirmModal({
      title: `Remove ${this.#paymentMethodName(method)}?`,
      action: "Remove",
      body: method.is_default ? __h("p", {}, "This is your default payment method. Removing it leaves your enterprise without a\n\t\t\t\t\t\t\tdefault, and no other payment method will be chosen automatically.") : __h("p", {}, "This payment method will no longer be used for billing.")
    });
    if (!removed) return;
    try {
      await authedApiRequest("DELETE", `/v2/enterprises/${this.props.enterpriseId}/billing/payment-methods/${method.id}`);
      this.#paymentMethods.fetch();
    } catch (err) {
      alert(ApiError.from(err).message, {
        type: "error"
      });
    }
  };

  /** @param {pendingVerification} pending */
  #cancelSetupIntent = async pending => {
    const cancelled = await confirmModal({
      title: `Cancel ${this.#paymentMethodName(pending)}?`,
      action: "Cancel setup",
      body: __h("p", {}, "The pending bank account will not be added as a payment method.")
    });
    if (!cancelled) return;
    try {
      await authedApiRequest("DELETE", `/v2/enterprises/${this.props.enterpriseId}/billing/setup-intents/${pending.id}`);
      this.#paymentMethods.fetch();
    } catch (err) {
      alert(ApiError.from(err).message, {
        type: "error"
      });
    }
  };

  /**
   * Renders a verified payment method, or a bank account still waiting on micro-deposit
   * verification.
   *
   * @param {paymentMethodResponse | pendingVerification} method
   */
  #row(method) {
    const pending = "verification_url" in method;
    const key = method.id;
    const name = this.#paymentMethodName(method);
    const detail = this.#paymentMethodDetail(method);

    /** @type {DropdownItem[]} */
    const items = pending ? [{
      icon: "check",
      text: "Verify bank account",
      href: method.verification_url,
      target: "_blank"
    }, {
      icon: "trash",
      text: "Cancel",
      kind: "negative",
      onSelect: () => this.#cancelSetupIntent(method)
    }] : [{
      icon: "check",
      text: "Set as default",
      disabled: method.is_default,
      onSelect: () => this.#setDefault(method)
    }, {
      icon: "trash",
      text: "Remove",
      kind: "negative",
      onSelect: () => this.#removePaymentMethod(method)
    }];
    return __h("li", {
      key: key,
      "class": css.listItem
    }, __h("div", {
      "class": css.listItemInfo
    }, __h("span", {
      "class": css.listItemName
    }, name), detail && __h("span", {
      "class": css.settingDescription
    }, detail)), __h("div", {
      "class": css.listItemActions
    }, pending ? __h("span", {
      "class": css.statusChip,
      "data-state": "pending"
    }, "Pending") : method.is_default && __h("span", {
      "class": css.statusChip,
      "data-state": "default"
    }, "Default"), __h(Dropdown, {
      id: "payment-method-menu-" + key,
      placement: "bottom-end",
      trigger: __h("button", {
        type: "button",
        "class": button.button,
        "data-level": "tertiary",
        "data-size": "small",
        popovertarget: "payment-method-menu-" + key,
        "aria-label": "Options for " + name
      }, __h(Icon, {
        name: "menu",
        size: 14
      })),
      items: items
    })));
  }
  render() {
    const paymentMethods = this.#paymentMethods;
    const data = paymentMethods.data?.data;
    const isEmpty = data && !data.payment_methods.length && !data.pending_setup_intents.length;
    const hasDefault = data?.payment_methods.some(method => method.is_default);
    return __h(Section, {
      title: "Billing",
      icon: "credit-card",
      actions: __h(Button, {
        size: "small",
        kind: "primary",
        level: "tertiary",
        disabled: !data,
        loading: this.#addPaymentMethod.loading,
        onClick: this.#addPaymentMethod.invoke
      }, "Add payment method")
    }, __h("div", {
      "class": css.list
    }, h(Data, {
      loading: paymentMethods.loading && !paymentMethods.data,
      error: paymentMethods.error,
      empty: isEmpty && __h("p", {
        "class": css.empty
      }, "No payment methods yet. Add a card or bank account to pay for Phaze."),
      children: data && [data.payment_methods.length > 0 && !hasDefault && __h(Banner, {
        icon: __h(Icon, {
          name: "warning"
        }),
        kind: "warning"
      }, "No default payment method is set. Choose one from a payment method's menu."), __h("ul", {
        "class": css.list
      }, [...data.payment_methods, ...data.pending_setup_intents].map(method => this.#row(method)))]
    })));
  }
}